import { MouseEvent, ReactNode, useCallback, useEffect } from 'react';

import { Box, Stack, StackProps, styled } from '@mui/material';
import clsx from 'clsx';
import copyFn from 'copy-to-clipboard';

import { tokens } from '../../../theme/generated/tokens';
import { shouldForwardProp } from '../../../utils/shouldForwardProp';
import { getThemeColor } from '../../../utils/theme';
import { Copy } from '../../icons';
import { Scroller } from '../../scroller';
import {
  HighlightedLanguage,
  useCodeHighlighter,
} from '../hooks/useCodeHighlighter';

export type PreviewSnippetProps = Omit<StackProps, 'onCopy'> & {
  code: string;
  actions?: ReactNode;
  language?: HighlightedLanguage;
  onCopy?: (code: string, e: MouseEvent) => void;
  renderPreview?: (code: string) => string;
  errorHighlight?: boolean | string;
  testId?: string;
};

const Root = styled(Stack, {
  target: 'DsPreviewSnippet-root',
})({
  backgroundColor: tokens.theme.bg.structural.code,
  color: tokens.primitives.colors.constants.white,
  borderRadius: tokens.theme.radius.sm,
  padding: '12px 0 12px 12px',
  position: 'relative',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
});

const CodeStack = styled(Stack, {
  target: 'DsPreviewSnippet-codeStack',
  shouldForwardProp: (prop) => prop !== 'highlightBar',
})<{ highlightBar?: string }>(({ theme, highlightBar }) => ({
  position: 'relative',
  '& a': {
    color: tokens.primitives.colors.blue[400],
    textDecoration: 'none',
    wordBreak: 'inherit',
    '&:hover': {
      color: tokens.primitives.colors.blue[500],
      textDecoration: 'underline',
    },
  },
  ...(highlightBar
    ? {
        paddingLeft: '8px',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: '3px',
          borderRadius: '8px',
          borderLeft: `3px solid ${getThemeColor(theme, highlightBar)}`,
        },
      }
    : {}),
}));

const CodeBox = styled(Box<'pre'>, {
  target: 'DsPreviewSnippet-codeBox',
})(({ theme }) => ({
  ...theme.typography.C12,
  border: 'none',
  flex: 1,
  margin: 0,
  padding: 0,
  paddingRight: 32,
  whiteSpace: 'inherit',
  wordBreak: 'inherit',
  tabSize: 4,
  hyphens: 'none',
}));

const Code = styled(Box<'code'>)(({ theme }) => ({
  ...theme.typography.C12,
  whiteSpace: 'inherit',
  wordBreak: 'inherit',
  tabSize: 4,
  hyphens: 'none',
}));

const StyledScroller = styled(Scroller, {
  target: 'DsPreviewSnippet-Scroller os-theme-light',
  shouldForwardProp,
})({});

const CopyCode = styled(Copy, {
  target: 'DsPreviewSnippet-copyCode',
})({
  cursor: 'pointer',
  fontSize: '16px',
  color: 'common.white',
  position: 'absolute',
  top: 8,
  right: 8,
  '&: hover': {
    color: 'inherit',
  },
});

export const PreviewSnippet = ({
  code,
  actions,
  className,
  language,
  onCopy,
  renderPreview,
  errorHighlight,
  testId,
  ...props
}: PreviewSnippetProps) => {
  const highlighter = useCodeHighlighter({ language, enabled: !!code });

  useEffect(() => {
    if (highlighter) {
      highlighter.highlightAll();
    }
  }, [code, highlighter]);

  const handleCopy = useCallback(
    (e: MouseEvent) => {
      copyFn(code);
      onCopy?.(code, e);
    },
    [code, onCopy]
  );

  const highlightBar = errorHighlight
    ? typeof errorHighlight === 'string'
      ? errorHighlight
      : tokens.primitives.colors.red[400]
    : undefined;

  return (
    <Root
      {...props}
      className={clsx(className, 'PreviewSnippet-Root')}
      data-testid={testId}
    >
      <StyledScroller
        options={{ scrollbars: { autoHide: 'move' } }}
        sx={{
          '&.os-host-overflow': { height: '100%' },
        }}
      >
        <CodeStack
          direction="row"
          justifyContent="space-between"
          gap={8}
          data-testid="preview-snippet"
          highlightBar={highlightBar}
        >
          <CodeBox
            component="pre"
            dangerouslySetInnerHTML={
              renderPreview ? { __html: renderPreview(code) } : undefined
            }
          >
            {!renderPreview ? (
              <Code
                component="code"
                className={language ? `language-${language}` : undefined}
              >
                {code}
              </Code>
            ) : null}
          </CodeBox>
        </CodeStack>
      </StyledScroller>
      {actions ?? (Boolean(onCopy) && <CopyCode onClick={handleCopy} />)}
    </Root>
  );
};
